import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { db } from '../services/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useAuth } from '../hooks/useAuth';
import { BADGES, DEFAULT_BADGE_IDS, ACHIEVEMENT_BADGE_IDS } from '../utils/constants';
import ProfileSubNav from '../components/ProfileSubNav';
import toast from 'react-hot-toast';
import './ProfilePage.css';

export default function ProfileSettingsPage() {
  const { user, userProfile } = useAuthContext();
  const { logout } = useAuth();

  // Username editing
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Badge selection
  const [badgeLoading, setBadgeLoading] = useState(false);
  const activeBadge = userProfile?.activeBadge || 'newbie';
  const earnedBadges = userProfile?.badges || ['newbie'];

  const handleEditStart = () => {
    setNewUsername(userProfile?.username || '');
    setIsEditing(true);
    setError(null);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleEditSave = async () => {
    const trimmed = newUsername.trim();
    if (trimmed.length < 3 || trimmed.length > 20) {
      setError('Username must be 3-20 characters');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      setError('Only letters, numbers, and underscores allowed');
      return;
    }

    const newLower = trimmed.toLowerCase();
    const oldLower = userProfile.usernameLower;

    if (newLower === oldLower) {
      setIsEditing(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await runTransaction(db, async (transaction) => {
        const newUsernameRef = doc(db, 'usernames', newLower);
        const newUsernameSnap = await transaction.get(newUsernameRef);

        if (newUsernameSnap.exists()) {
          throw new Error('Username is already taken.');
        }

        const oldUsernameRef = doc(db, 'usernames', oldLower);
        transaction.delete(oldUsernameRef);

        transaction.set(newUsernameRef, {
          uid: user.uid,
          email: userProfile.emailLower || userProfile.email?.toLowerCase() || '',
          createdAt: serverTimestamp(),
        });

        const userRef = doc(db, 'users', user.uid);
        transaction.update(userRef, {
          username: trimmed,
          usernameLower: newLower,
          updatedAt: serverTimestamp(),
        });
      });

      toast.success('Username updated!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating username:', err);
      setError(err.message || 'Failed to update username.');
    } finally {
      setLoading(false);
    }
  };

  // Change Password
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    setPasswordError(null);
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmNewPassword } = passwordData;

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordLoading(true);
    setPasswordError(null);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      toast.success('Password updated!');
      setShowChangePassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      console.error('Password change error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordError('Current password is incorrect');
      } else if (err.code === 'auth/too-many-requests') {
        setPasswordError('Too many attempts. Please try again later.');
      } else {
        setPasswordError(err.message || 'Failed to update password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSelectBadge = async (badgeId) => {
    if (badgeId === activeBadge || badgeLoading) return;

    setBadgeLoading(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        activeBadge: badgeId,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Badge updated to ${BADGES[badgeId]?.name || badgeId}!`);
    } catch (err) {
      console.error('Error updating badge:', err);
      toast.error('Failed to update badge');
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out');
  };

  if (!userProfile) {
    return <div className="profile-page"><p>Loading profile...</p></div>;
  }

  return (
    <div className="profile-page">
      <h1>Profile</h1>
      <ProfileSubNav />

      <div className="profile-card">
        <div className="profile-section">
          <label>Username</label>
          {isEditing ? (
            <div className="edit-username">
              <input
                type="text"
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  setError(null);
                }}
                maxLength={20}
                autoFocus
              />
              {error && <span className="profile-error">{error}</span>}
              <div className="edit-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleEditSave}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleEditCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-value">
              <span>{userProfile.username}</span>
              <button className="btn btn-secondary btn-sm" onClick={handleEditStart}>
                Edit
              </button>
            </div>
          )}
        </div>

        <div className="profile-section">
          <label>Name</label>
          <div className="profile-value">
            <span>{userProfile.displayName || 'N/A'}</span>
          </div>
        </div>

        <div className="profile-section">
          <label>Email</label>
          <div className="profile-value">
            <span>{userProfile.email || 'N/A'}</span>
          </div>
        </div>

        <div className="profile-section">
          <label>Phone</label>
          <div className="profile-value">
            <span>{userProfile.phone || user?.phoneNumber || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="profile-card">
        <div className="profile-section" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <label>Password</label>
          {showChangePassword ? (
            <div className="change-password-form">
              <input
                type="password"
                placeholder="Current password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                autoFocus
              />
              <input
                type="password"
                placeholder="New password (min 6 characters)"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={passwordData.confirmNewPassword}
                onChange={(e) => handlePasswordChange('confirmNewPassword', e.target.value)}
              />
              {passwordError && <span className="profile-error">{passwordError}</span>}
              <div className="edit-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
                    setPasswordError(null);
                  }}
                  disabled={passwordLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-value">
              <span>••••••••</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowChangePassword(true)}>
                Change
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="profile-card badge-card">
        <h2>Badge</h2>
        <div className="active-badge-display">
          <span className="active-badge-emoji">{BADGES[activeBadge]?.emoji || '🐣'}</span>
          <span className="active-badge-name">{BADGES[activeBadge]?.name || 'Newbie'}</span>
        </div>

        <h3 className="badge-section-title">Avatars</h3>
        <p className="badge-section-desc">Choose your avatar</p>
        <div className="badge-grid">
          {DEFAULT_BADGE_IDS.map((id) => {
            const badge = BADGES[id];
            const isActive = id === activeBadge;
            return (
              <button
                key={id}
                className={`badge-tile ${isActive ? 'badge-active' : ''}`}
                onClick={() => handleSelectBadge(id)}
                disabled={badgeLoading}
                title={badge.name}
              >
                <span className="badge-tile-emoji">{badge.emoji}</span>
                <span className="badge-tile-name">{badge.name}</span>
              </button>
            );
          })}
        </div>

        <h3 className="badge-section-title">Achievements</h3>
        <p className="badge-section-desc">Earn badges by hitting milestones</p>
        <div className="badge-grid">
          {ACHIEVEMENT_BADGE_IDS.map((id) => {
            const badge = BADGES[id];
            const isEarned = earnedBadges.includes(id);
            const isActive = id === activeBadge;
            return (
              <button
                key={id}
                className={`badge-tile ${isEarned ? '' : 'badge-locked'} ${isActive ? 'badge-active' : ''}`}
                onClick={() => isEarned && handleSelectBadge(id)}
                disabled={!isEarned || badgeLoading}
                title={isEarned ? badge.name : `${badge.name} — ${badge.description}`}
              >
                <span className="badge-tile-emoji">{isEarned ? badge.emoji : '🔒'}</span>
                <span className="badge-tile-name">{badge.name}</span>
                <span className="badge-tile-criteria">{badge.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="profile-actions">
        <button className="btn btn-danger" onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
